import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { addDays, isSameMonth, startOfDay, startOfMonth } from 'date-fns';
import {
    forwardRef, useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import CustomTags from '../customfields/CustomTags.jsx';
import { ObjectTypes } from '../../lib/ObjectTypes.js';
import { isMobile } from '../../lib/Utils.js';
import { minutesAfterDayStart } from '~/lib/DateTime';
import useEventColors from '~/lib/useEventColors';
import CircleClockIcon from '~/components/icons/CircleClockIcon';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline/index';
import PaymentStatusIcon from '~/components/payments/PaymentStatusIcon';
import PaymentStatusLabel from '~/components/payments/PaymentStatusLabel';
import PencilSquareIcon from '~/components/icons/PencilSquareIcon';

// import adaptivePlugin from '@fullcalendar/adaptive'

function calculateMaxConcurrentEvents(events, viewStart, viewEnd) {
    const timeEvents = events.filter(ev => {
        if (!ev.start || !ev.end || ev.allDay || ev.groupId === 'availability')
            return false;
        const start = new Date(ev.start).getTime();
        const end = new Date(ev.end).getTime();
        return start < viewEnd && end > viewStart;
    });
    if (timeEvents.length === 0) return 0;

    const points = [];
    for (const ev of timeEvents) {
        const start = new Date(ev.start).getTime();
        const end = new Date(ev.end).getTime();
        if (start < end) {
            points.push({ time: start, type: 1 });
            points.push({ time: end, type: -1 });
        }
    }

    points.sort((a, b) => a.time - b.time || a.type - b.type);

    let max = 0;
    let current = 0;
    for (const point of points) {
        current += point.type;
        if (current > max) max = current;
    }

    return max;
}

function getAdaptiveDayMinWidth(maxConcurrent) {
    if (maxConcurrent >= 7) return 240;
    if (maxConcurrent >= 5) return 200;
    if (maxConcurrent >= 4) return 140;
    return null;
}

function minutesToPixels(minutes) {
    return Math.floor(0.8333333 * minutes) * 2;
}

function getEventTitle(ev) {
    let name = '';
    if (ev.name && ev.event_type?.name) {
        name = `${ev.name}: ${ev.event_type?.name}`;
    } else if (ev.name) {
        name = ev.name;
    } else {
        name = ev.event_type?.name;
    }

    if (ev.event_type?.is_class && ev.max_attendees) {
        if (ev.settings?.label) {
            name += `: ${ev.settings.label}`;
        }
        name += ` (${ev.attendees_count}/${ev.max_attendees})`;
    }

    return name;
}

function renderEventContent(eventInfo, view, hasMultipleResources) {
    let style = {};
    if (view === 'dayGridMonth') {
        style = {
            backgroundColor: eventInfo.backgroundColor,
            color: eventInfo.textColor,
        };
    }

    const showPaymentStatusIcon =
        view !== 'dayGridMonth' && view?.indexOf('list') < 0;
    const showPaymentLabel = view?.indexOf('list') >= 0;
    const showResourceName =
        eventInfo.event.extendedProps.all_bookable_resource_ids?.length > 1
    || (hasMultipleResources && (view === 'dayGridMonth' || view?.indexOf('list') >= 0 || view === 'timeGridWeek'));

    return (
        <div className="fc-event-main-frame rounded" style={style}>
            <div className="fc-event-time flex items-center justify-between">
                {eventInfo.timeText}
                {showPaymentStatusIcon && (
                    <PaymentStatusIcon
                        paymentStatus={
                            eventInfo.event.extendedProps.payment_status
                        }
                    />
                )}
                {eventInfo.event.extendedProps.is_editable && (
                    <PencilSquareIcon className="w-3 h-3 ml-1 flex-shrink-0" />
                )}
            </div>
            <div
                className="fc-event-title-container"
                title={eventInfo.event.title}
            >
                <div className="fc-event-title fc-sticky">
                    <div className="flex items-center">
                        {!!eventInfo.event.extendedProps.is_pending && (
                            <CircleClockIcon className="w-3 h-3 mr-1" />
                        )}
                        <div>
                            {eventInfo.event.title}
                            {showResourceName && !!eventInfo.event.extendedProps.resource_name && (
                                <>
                                    {' '}
                                    (
                                    {
                                        eventInfo.event.extendedProps
                                            .resource_name
                                    }
                                    )
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex space-x-1">
                        {showPaymentLabel && (
                            <PaymentStatusLabel
                                paymentStatus={
                                    eventInfo.event.extendedProps.payment_status
                                }
                            />
                        )}
                        {!eventInfo.event.extendedProps.is_class && (
                            <CustomTags
                                id={eventInfo.event.id}
                                type={ObjectTypes.BOOKING_EVENT}
                                fields={
                                    eventInfo.event.extendedProps.custom_fields
                                }
                                compact={true}
                                className="mt-1"
                            />
                        )}
                        {eventInfo.event.extendedProps.is_class && (
                            <CustomTags
                                id={eventInfo.event.id}
                                type={ObjectTypes.CLASS}
                                fields={
                                    eventInfo.event.extendedProps.custom_fields
                                }
                                compact={true}
                                className="mt-1"
                            />
                        )}
                        {!!eventInfo.event.extendedProps.client?.id && (
                            <CustomTags
                                id={eventInfo.event.extendedProps.client.id}
                                type={ObjectTypes.CLIENT}
                                fields={
                                    eventInfo.event.extendedProps.client
                                        .custom_fields
                                }
                                compact={true}
                                className="mt-1"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const BigCalendar = ({
    setSelectedDate,
    selectedDate,
    setViewStartDate,
    setViewEndDate,
    onEventClicked,
    onClassClicked,
    onBlockedClicked,
    onSelectTime,
    bookableResources,
    events,
    hours,
    className,
    height,
    onDragEnd,
    onClickAdd,
    weekStartsOn,
    use24HourTime,
}) => {
    const calendarRef = useRef();
    const [currentDateInfo, setCurrentDateInfo] = useState(null);
    const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
    const [currentViewType, setCurrentViewType] = useState(null);
    const {
        getEventColor,
        getTextColor,
        resourceColors,
        typeColors,
        colorBasedOn,
    } = useEventColors();

    if (!events?.map) {
        (events.map = () => []);
    }

    let eventData = useMemo(() => {
        let ret = events.map(ev => ({
            title: getEventTitle(ev, currentDateInfo?.view?.type),
            start: ev.client_start || ev.start,
            end: ev.client_end || ev.end,
            padding_start:
                (ev.event_type?.is_class
                    ? ev.event_type?.padding_start
                    : ev.padding_start) || 0,
            padding_end:
                (ev.event_type?.is_class
                    ? ev.event_type?.padding_end
                    : ev.padding_end) || 0,
            is_pending: !!ev.pending_until,
            client: ev.client,
            startEditable: !!ev.client /*only booking events*/,
            event_id: ev.id,
            custom_fields: ev.custom_fields,
            type: ev.event_type?.type,
            event_type_id: ev.event_type?.id,
            is_class: ev.event_type?.is_class,
            is_editable: ev.is_editable || false,
            payment_status: ev.payment_status,
            allDay: ev.event_type?.duration > 0 && ev.event_type?.is_all_day,
            resourceIds: ev.all_bookable_resource_ids || [
                ev.bookable_resource?.id,
            ],
            all_bookable_resource_ids: ev.all_bookable_resource_ids,
            resource_name: ev.bookable_resource?.name,
            bookable_resource_id: ev.all_bookable_resource_ids
                ? ev.all_bookable_resource_ids[0]
                : ev.bookable_resource?.id,
            backgroundColor: getEventColor(ev),
            textColor: getTextColor(ev),
        }));

        if (currentDateInfo?.view?.type !== 'resourceTimeGridDay') {
            // Create a Map to store unique events based on title, start, and end
            const uniqueEvents = new Map();

            ret.forEach(event => {
                const key = `${event.title}-${event.start}-${event.end}-${event.event_type_id ? Math.random() : ''}`;
                if (!uniqueEvents.has(key)) {
                    uniqueEvents.set(key, event);
                }
            });

            ret = Array.from(uniqueEvents.values());
        }

        return ret;
    }, [
        events,
        JSON.stringify(resourceColors),
        colorBasedOn,
        JSON.stringify(typeColors),
        currentDateInfo?.view?.type,
    ]);

    const resourceData = (bookableResources || []).map((resource, idx) => ({
        id: resource.id,
        title: resource.name,
        order: idx,
    }));

    const earliestClientEvent = useMemo(() => {
        let earliest = null;
        for (let ev of eventData) {
            // We only want real client events
            if ((!ev.client && !ev.is_class) || !currentDateInfo) continue;

            const eventStart = new Date(ev.start);
            if (
                eventStart < currentDateInfo.start ||
                eventStart > currentDateInfo.end
            ) {
                // Skip it, it's not in the view
                continue;
            }

            const thisDayStartMinutes = minutesAfterDayStart(eventStart);

            if (!earliest || earliest > thisDayStartMinutes) {
                earliest = thisDayStartMinutes;
            }
        }

        if (earliest) {
            const hours = parseInt(earliest / 60)
                .toString()
                .padStart(2, '0');
            const minutes = (earliest % 60).toString().padStart(2, '0');
            return `${hours}:${minutes}:00`;
        } else {
            return '08:00:00';
        }
    }, [eventData, currentDateInfo]);

    const dayMinWidth = useMemo(() => {
        if (!currentDateInfo?.start || !currentDateInfo?.end) return null;
        const adaptiveWidth = getAdaptiveDayMinWidth(
            calculateMaxConcurrentEvents(
                eventData,
                currentDateInfo.start.getTime(),
                currentDateInfo.end.getTime(),
            ),
        );
        if (
            currentDateInfo?.view?.type === 'resourceTimeGridDay' &&
            resourceData.length > 7
        ) {
            return Math.max(adaptiveWidth, 140);
        }
        return adaptiveWidth;
    }, [eventData, currentDateInfo?.start, currentDateInfo?.end, currentDateInfo?.view?.type, resourceData.length]);


    const earliestClientEventRef = useRef(earliestClientEvent);
    earliestClientEventRef.current = earliestClientEvent;

    const scrollToEarliestEvent = useCallback(() => {
        requestAnimationFrame(() => {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.scrollToTime(earliestClientEventRef.current);
            }
        });
    }, []);

    // Identifies the range currently on screen. Only scroll when we move to a
    // different view/range, or when the events for that range finish loading
    // and give us a new earliest time -- not on every re-render.
    const scrollKey = currentDateInfo
        ? `${currentDateInfo.view.type}|${currentDateInfo.start.toISOString()}`
        : null;

    useEffect(() => {
        if (!scrollKey) return;
        scrollToEarliestEvent();
    }, [scrollKey, earliestClientEvent, scrollToEarliestEvent]);

    const isSummaryView =
        currentDateInfo?.view?.type === 'dayGridMonth' ||
        currentDateInfo?.view?.type?.indexOf('list') > -1;

    // In monthly view don't show blocked off times, only booked events
    if (isSummaryView) {
        eventData = eventData.filter(ev => !!ev.type);
    }

    if (Array.isArray(hours)) {
        if (hours.length === 0) {
            // Make sure if there is no availability we block off everything
            hours = [
                {
                    start: '1986-03-11T13:00',
                    end: '1986-03-11T14:00',
                },
            ];
        }
        const visibleResourceIds = (bookableResources || []).map(r => r.id);
        hours = hours.filter(
            h => visibleResourceIds.indexOf(h.bookableResourceId) > -1,
        );
        eventData = [
            ...eventData,
            ...hours.map(hoursSet => ({
                groupId: 'availability',
                display: 'inverse-background',
                start: hoursSet.start,
                end: hoursSet.end,
                resourceIds: [hoursSet.bookableResourceId],
            })),
        ];
    }

    function onDateSet(dateInfo) {
        setCurrentDateInfo(dateInfo);

        let newSelectedDate = dateInfo.start;
        if (dateInfo.view.type === 'dayGridMonth') {
            //Start might actually be the last month, add 7 days then do start of month
            newSelectedDate = startOfMonth(addDays(newSelectedDate, 7));
            if (isSameMonth(newSelectedDate, new Date())) {
                // Current month? Choose today!
                newSelectedDate = startOfDay(new Date());
            }
        }

        setSelectedDate(newSelectedDate);

        if (isInitialLoadDone) {
            localStorage.setItem('agenda-view', dateInfo.view.type);
        }

        setViewStartDate(dateInfo.start);
        setViewEndDate(dateInfo.end);
    }

    useEffect(() => {
        if (selectedDate && currentDateInfo?.view.type) {
            queueMicrotask(() => {
                const calendarApi = calendarRef.current.getApi();
                calendarApi.changeView(
                    currentDateInfo?.view.type,
                    selectedDate,
                );
            });
        }
    }, [selectedDate?.toISOString()]);

    useEffect(() => {
        setIsInitialLoadDone(true);
    }, []);

    function onEventMounted({ event, el }) {
        // Add padding!
        if (!isSummaryView && event.extendedProps.padding_start > 0) {
            el.className += ' has-padding-top';
            const padding = document.createElement('div');
            padding.className = 'event-padding-top';
            padding.style.backgroundColor = getEventColor(event);
            padding.style.height = `${minutesToPixels(event.extendedProps.padding_start)}px`;
            padding.style.marginTop = `-${minutesToPixels(event.extendedProps.padding_start)}px`;
            el.insertBefore(padding, el.firstChild);
        }

        if (!isSummaryView && event.extendedProps.padding_end > 0) {
            el.className += ' has-padding-bottom';
            const padding = document.createElement('div');
            padding.className = 'event-padding-bottom';
            padding.style.backgroundColor = getEventColor(event);
            padding.style.height = `${minutesToPixels(event.extendedProps.padding_end)}px`;
            padding.style.marginBottom = `-${minutesToPixels(event.extendedProps.padding_end)}px`;
            el.appendChild(padding);
        }

        if (event.extendedProps.type) {
            el.className += ` ${event.extendedProps.type}-event`;
        } else {
            el.className += ' blocking-event';
        }
    }

    function getEventFromCalendarEvent(ev) {
        const matchingEvents = events.filter(
            other =>
                ev.extendedProps.event_id === other.id &&
                !other.event_type?.is_class,
        );
        return matchingEvents[0];
    }

    function getClassFromCalendarEvent(ev) {
        const matchingEvents = events.filter(
            other =>
                ev.extendedProps.event_id === other.id &&
                other.event_type?.is_class,
        );
        return matchingEvents[0];
    }

    const renderEventContentWithView = eventInfo =>
        renderEventContent(eventInfo, currentDateInfo?.view?.type, resourceData?.length > 1);

    const dailyView =
        resourceData.length > 1 ? 'resourceTimeGridDay' : 'timeGridDay';

    // Get saved view preference from localStorage, fallback to default based on device
    const getInitialView = () => {
        const savedView = localStorage.getItem('agenda-view');
        if (savedView) {
            return savedView;
        }
        return isMobile() ? dailyView : 'timeGridWeek';
    };

    return (
        <FullCalendar
            ref={calendarRef}
            schedulerLicenseKey="0520633326-fcs-1773138385"
            plugins={[
                timeGridPlugin,
                interactionPlugin,
                dayGridPlugin,
                listPlugin,
                resourceTimeGridPlugin,
                scrollGridPlugin,
            ]}
            initialView={getInitialView()}
            customButtons={{
                add: {
                    text: '+',
                    click: onClickAdd,
                },
            }}
            views={{
                listTwoWeek: {
                    type: 'list',
                    duration: { days: 28 },
                    buttonText: 'List',
                },
            }}
            headerToolbar={{
                start: 'title', // will normally be on the left. if RTL, will be on the right
                center: '',
                end: `today prev,next ${dailyView},${isMobile() ? '' : 'timeGridWeek,dayGridMonth,'}listTwoWeek`, // will normally be on the right. if RTL, will be on the left
            }}
            datesSet={onDateSet}
            nowIndicator={true}
            weekends={true}
            events={eventData}
            resources={resourceData}
            resourceOrder="order"
            eventContent={renderEventContentWithView}
            eventClick={ev => {
                if (ev.event.extendedProps.client) {
                    onEventClicked(getEventFromCalendarEvent(ev.event));
                } else if (ev.event.extendedProps.is_class) {
                    onClassClicked(getClassFromCalendarEvent(ev.event));
                } else if (ev.event.extendedProps.is_editable) {
                    onBlockedClicked(getEventFromCalendarEvent(ev.event));
                }
            }}
            height={height ? `${height}px` : null}
            viewClassNames={className}
            eventTimeFormat={
                use24HourTime
                    ? {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: false,
                      }
                    : {
                          hour: 'numeric',
                          minute: '2-digit',
                          omitZeroMinute: true,
                          meridiem: 'narrow',
                      }
            }
            slotLabelFormat={
                use24HourTime
                    ? {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: false,
                      }
                    : {
                          hour: 'numeric',
                          minute: '2-digit',
                          omitZeroMinute: true,
                          meridiem: 'short',
                      }
            }
            displayEventEnd={false}
            eventDidMount={onEventMounted}
            editable={true}
            eventDurationEditable={false}
            eventDrop={({ revert, event, newResource }) => {
                onDragEnd({
                    newClientStart: event.start,
                    newClientEnd: event.end,
                    event: getEventFromCalendarEvent(event),
                    revert: revert,
                    resource: newResource,
                });
            }}
            dragRevertDuration={2000}
            scrollTime={'08:00:00'}
            scrollTimeReset={true /*we handle this manually based on events*/}
            snapDuration={'00:15:00'}
            slotDuration={
                '00:15:00' /*update minutesToPixels if this ever changes*/
            }
            slotLabelInterval={'01:00:00'}
            firstDay={typeof weekStartsOn === 'undefined' ? 1 : weekStartsOn}
            listDayFormat={{
                // will produce something like "Tuesday, September 18, 2018"
                month: 'long',
                year: 'numeric',
                day: 'numeric',
                weekday: 'long',
            }}
            dayMinWidth={dayMinWidth || 50}
            selectable={true}
            select={ev => {
                if (onSelectTime) onSelectTime(ev);
                const calendarApi = calendarRef.current.getApi();
                calendarApi.unselect();
            }}
        />
    );
};

export default BigCalendar;
